import { lerUltimaEscolha, guardarUltimaEscolha } from '../utils/ultimaEscolha';
import { abrirSeletorDeData } from '../utils/abrirSeletorDeData';
import { ehLancamentoCancelado } from '../utils/duplicata';
import React, { useState, useEffect, useRef } from 'react';
import { Transaction, TransactionType, PaymentType } from '../types/Transaction';
import { PaymentMethod } from '../types/PaymentMethod';
import { Category } from '../types/Category';
import { Investment } from '../types/Investment';
import { Goal } from '../types/Goal';
import {
  getPaymentMethods,
  getInvestments, getGoals, updateInvestment, updateGoal,
  parseQuickEntry, QuickEntry as LancamentoLido, ParsedInvestment,
} from '../services/api';
import QuickEntry, { QuickEntryPrefill } from './QuickEntry';
import { useTemCasa } from '../utils/temCasa';
import { reduzirDataUrl } from '../utils/reduzirImagem';
import './TransactionForm.css';
import { mensagemDeErro } from '../utils/mensagemDeErro';
import Toast from './Toast';
/* O MESMO número que o schema do servidor usa — ver utils/limites.ts. */
import { LIMITES } from '../utils/limites';
import { proximoPagamento, hojeISOLocal, datasAoTrocarMetodo } from '../utils/dataDoCartao';
import { rotuloDoMes } from './SeletorDeMes';
import { criarLembreteDeTransacao } from '../services/api';
import { formatBRL } from '../utils/format';
import { useFechavelComVoltar } from '../contexts/VoltarDoCelular';
import { useGerenciadorDeCategorias } from './GerenciadorDeCategorias';

interface DebitSource {
  id: string;
  type: 'investment' | 'goal';
  name: string;
  balance: number;
  raw: Investment | Goal;
}

interface TransactionFormProps {
  onSubmit: (data: Omit<Transaction, 'id' | 'createdAt'>, scope?: 'this' | 'future') => Promise<void>;
  editingTransaction: Transaction | null;
  /** Avisa que um lembrete foi criado e ligado a este lançamento. */
  onLembreteCriado?: () => void;
  onCancel: () => void;
  paymentMethodsRefresh?: number;
  debitSourcesRefresh?: number;
  categories?: Category[];
  onCategoryCreated?: () => void;
  onCategoryDeleted?: () => void;
  onDebitApplied?: () => void;
  /**
   * "investi 5 mil em CDB" escrito no campo único não é gasto: vira aplicação.
   * O componente só repassa — quem grava e recarrega é o App, que é dono das
   * listas de investimento e de transação.
   */
  onCreateInvestment?: (inv: ParsedInvestment) => Promise<void>;
  /**
   * O mês que a tela está mostrando, 'AAAA-MM' (15/09). É o mês de "só neste
   * mês" quando o valor de uma mensal muda na edição.
   */
  mesExibido?: string;
}

const DEFAULT_PAYMENT_METHOD = '';

const localDateString = () => {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};


const TransactionForm: React.FC<TransactionFormProps> = ({
  onSubmit,
  editingTransaction,
  onLembreteCriado,
  onCancel,
  paymentMethodsRefresh,
  debitSourcesRefresh,
  categories = [],
  onCategoryCreated,
  onCategoryDeleted,
  onCreateInvestment,
  onDebitApplied,
  mesExibido,
}) => {
  const [availableMethods, setAvailableMethods] = useState<PaymentMethod[]>([]);
  /* O erro do servidor vai para o toast; ver utils/mensagemDeErro.ts. */
  const [erroToast, setErroToast] = useState<string | null>(null);
  /** Feedback do botão de criar lembrete a partir desta despesa. */
  const [lembreteAviso, setLembreteAviso] = useState<string | null>(null);
  const [criandoLembrete, setCriandoLembrete] = useState(false);

  /**
   * Cria um lembrete já ligado a ESTA despesa.
   *
   * Só despesa: lembrete existe para não esquecer de PAGAR, e o servidor
   * recusa receita. Ligado, o lançamento passa a mostrar ⏳ na lista até ela
   * marcar o lembrete como feito no mês.
   *
   * Não sabemos aqui se já existe um lembrete ligado — o formulário não
   * carrega lembretes. O servidor responde 409 nesse caso, e a mensagem dele
   * vai direto para a tela. Buscar só para desabilitar um botão pagaria uma
   * requisição a cada abertura de formulário para evitar um aviso.
   */
  const criarLembrete = async () => {
    if (!editingTransaction || criandoLembrete) return;
    setCriandoLembrete(true);
    setLembreteAviso(null);
    try {
      await criarLembreteDeTransacao(editingTransaction.id);
      setLembreteAviso('Lembrete criado e ligado a este lançamento.');
      // A lista precisa passar a mostrar o anel, e o saldo precisa recontar.
      onLembreteCriado?.();
    } catch (e: any) {
      setLembreteAviso(mensagemDeErro(e, 'Não consegui criar o lembrete.'));
    } finally {
      setCriandoLembrete(false);
    }
  };
  const [submitting, setSubmitting] = useState(false);
  const [showMonthlyModal, setShowMonthlyModal] = useState(false);
  const [pendingTransactionData, setPendingTransactionData] = useState<Omit<Transaction, 'id' | 'createdAt'> | null>(null);
  const [debitSources, setDebitSources] = useState<DebitSource[]>([]);
  const [debitSourceId, setDebitSourceId] = useState('');

  // Receipt scan states
  const [scanning, setScanning] = useState(false);
  const [scanPreview, setScanPreview] = useState<string | null>(null);
  const [scanError, setScanError] = useState<string | null>(null);
  /** Várias compras lidas de uma foto: vão para a fila do campo único. */
  const [leitura, setLeitura] = useState<{ entries: LancamentoLido[]; vez: number } | null>(null);
  const [aiFilledFields, setAiFilledFields] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Crop states
  const [cropData, setCropData] = useState<{ dataUrl: string; mimeType: string } | null>(null);
  const [cropSel, setCropSel] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null);
  const cropImgRef = useRef<HTMLImageElement>(null);
  const cropContainerRef = useRef<HTMLDivElement>(null);

  const DEBIT_PREFIX = '__debit__';
  const WITHDRAW_PREFIX = '__withdraw__';
  // Item 9b: uma transação é "de fonte" pela coluna debitDirection. O teste do
  // prefixo fica como fallback para linhas gravadas antes do backfill do 9a.
  /** Só o teste do rótulo, para o estado do formulário (que é string pura). */
  const isPrefixedLabel = (pm: string) =>
    pm.startsWith(DEBIT_PREFIX) || pm.startsWith(WITHDRAW_PREFIX);

  const isSourceTransaction = (t?: Transaction | null) => {
    if (!t) return false;
    if (t.debitDirection) return true;
    const pm = t.paymentMethod ?? '';
    return pm.startsWith(DEBIT_PREFIX) || pm.startsWith(WITHDRAW_PREFIX);
  };

  // New category inline form

  useEffect(() => {
    getPaymentMethods().then(methods => {
      setAvailableMethods(methods);
      setFormData(prev => {
        // Não sobrescreve se for uma transação debitada/retirada de investimento/reserva
        if (isPrefixedLabel(prev.paymentMethod)) return prev;
        const exists = methods.some(m => m.id === prev.paymentMethod);
        if (!exists && methods.length > 0) {
          // O formulário ainda está no método vazio de nascença: é aqui que a
          // lembrança do último lançamento entra pela primeira vez.
          return comUltimaEscolha({ ...prev, paymentMethod: methods[0].id }, methods);
        }
        return prev;
      });
    }).catch(() => {});
  // `comUltimaEscolha` é recriada a cada render e só lê o `localStorage` e os
  // métodos recebidos por parâmetro: listá-la aqui recarregaria os métodos a
  // cada tecla digitada.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentMethodsRefresh]);

  // Carrega investimentos e reservas disponíveis para débito/retirada
  useEffect(() => {
    const isEditingSource = isSourceTransaction(editingTransaction);
    // Se editando uma transação normal, não precisa carregar fontes
    if (editingTransaction && !isEditingSource) {
      setDebitSources([]);
      setDebitSourceId('');
      return;
    }
    Promise.all([getInvestments(), getGoals()]).then(([invs, goals]) => {
      const sources: DebitSource[] = [
        ...invs.filter(i => i.amount > 0).map(i => ({
          id: `inv_${i.id}`,
          type: 'investment' as const,
          name: `${i.name} (${i.type})`,
          balance: i.amount,
          raw: i,
        })),
        ...goals
          .filter(g => g.type === 'reserve' && g.savedAmount > 0)
          .map(g => ({
            id: `goal_${g.id}`,
            type: 'goal' as const,
            name: `Reserva: ${g.name}`,
            balance: g.savedAmount,
            raw: g,
          })),
      ];
      setDebitSources(sources);

      if (isEditingSource) {
        // Pré-seleciona a fonte correspondente ao paymentMethod salvo
        // Item 9b: casa por ID da fonte, não por rótulo. Renomear o
        // investimento ou a meta não quebra mais a pré-seleção.
        const et = editingTransaction!;
        let match = et.debitSourceId
          ? sources.find(s => s.id === `${et.debitSourceType === 'goal' ? 'goal' : 'inv'}_${et.debitSourceId}`)
          : undefined;
        if (!match) {
          // Fallback para linhas antigas, ainda com rótulo em payment_method.
          const pm = et.paymentMethod ?? '';
          const savedName = pm.startsWith(DEBIT_PREFIX)
            ? pm.slice(DEBIT_PREFIX.length)
            : pm.slice(WITHDRAW_PREFIX.length);
          match = sources.find(s => s.name === savedName);
        }
        setDebitSourceId(match?.id ?? '');
      } else {
        setDebitSourceId(prev => sources.some(s => s.id === prev) ? prev : '');
      }
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingTransaction, debitSourcesRefresh]);

  const EMPTY_FORM = {
    type: TransactionType.EXPENSE,
    paymentType: PaymentType.VARIABLE,
    description: '',
    value: '',
    hasInstallments: false,
    installments: '',
    currentInstallment: '',
    isMonthly: false,
    endDate: '',
    paymentMethod: DEFAULT_PAYMENT_METHOD,
    date: localDateString(),
    /*
     * A data da compra. VAZIO quer dizer "igual ao pagamento" — não "hoje".
     *
     * Preencher com a data de hoje por padrão faria toda transação comum
     * nascer com uma compra explícita, e qualquer correção do pagamento
     * depois inventaria uma divergência. Só existe compra diferente quando
     * alguém a escreve: ela, ou o botão da fatura.
     */
    purchaseDate: '',
    categoryId: '',
    /*
     * Vazio significa HERDAR do método, não "pessoal".
     *
     * A distinção é a feature: quem não mexe no seletor está aceitando o
     * padrão do cartão, e é isso que faz a marca não virar atrito. Mandar
     * `pessoal` explícito aqui apagaria a herança.
     */
    escopo: '' as '' | 'casa' | 'pessoal',
  };

  const [formData, setFormData] = useState(EMPTY_FORM);

  /**
   * O formulário de um lançamento NOVO: o vazio, já com o método e a categoria
   * do último lançamento daquele tipo (05/10).
   *
   * Os ids lembrados são conferidos contra o que existe AGORA — método ou
   * categoria apagados não voltam. Sem lembrança válida, cai no que era antes:
   * o primeiro método da lista e nenhuma categoria.
   *
   * Método com dia de fatura leva as datas junto, pela mesma função de quando
   * ela troca o método à mão (`datasAoTrocarMetodo`): abrir com o cartão já
   * escolhido e o pagamento em "hoje" seria o formulário mentindo a data.
   *
   * `metodos` e `cats` entram por parâmetro porque o primeiro uso acontece
   * dentro do efeito que acabou de carregar os métodos, antes do estado.
   */
  const comUltimaEscolha = <T extends typeof EMPTY_FORM>(
    base: T, metodos: PaymentMethod[] = availableMethods, cats: Category[] = categories,
  ): T => {
    const lembrada = lerUltimaEscolha(base.type === TransactionType.INCOME ? 'income' : 'expense');
    // Lembrado → o que já estava no formulário → o primeiro da lista.
    const metodo = metodos.find(m => m.id === lembrada?.paymentMethod)
      ?? metodos.find(m => m.id === base.paymentMethod)
      ?? metodos[0];
    // '' lembrado é escolha ("o último foi sem categoria"); categoria apagada
    // não é — aí fica o que o formulário já tinha.
    const categoryId = !lembrada ? base.categoryId
      : lembrada.categoryId === '' || cats.some(c => c.id === lembrada.categoryId)
        ? lembrada.categoryId
        : base.categoryId;
    if (!metodo) return { ...base, categoryId };
    const lembrouOMetodo = metodo.id === lembrada?.paymentMethod;
    const d = lembrouOMetodo && base.date
      ? datasAoTrocarMetodo(
        { date: base.date, purchaseDate: base.purchaseDate || null },
        metodo.billingDay ?? null, metodo.closingDay ?? null,
      )
      : null;
    return {
      ...base, paymentMethod: metodo.id, categoryId,
      ...(d ? { date: d.date, purchaseDate: d.purchaseDate ?? '' } : {}),
    };
  };

  /**
   * D19 — o campo único é a opção principal; o formulário de 8 campos fica
   * recolhido abaixo. Ao EDITAR uma transação existente ele abre sozinho,
   * porque aí não há texto para interpretar.
   */
  const [formAberto, setFormAberto] = useState(false);

  /*
   * O voltar do celular fecha o formulário aberto pelo "Preencher
   * manualmente". Só quando NÃO está editando: em edição o formulário vive
   * dentro do `EdicaoModal`, que já se registra por conta própria — dois
   * registros fechariam duas coisas com um toque só.
   */
  useFechavelComVoltar(
    formAberto && !editingTransaction,
    React.useCallback(() => setFormAberto(false), []),
  );
  /**
   * Sobe a cada gravação. O `QuickEntry` escuta para limpar a revisão junto —
   * salvar pelo formulário resolve a frase interpretada, e deixar os dois
   * abertos convida a lançar duas vezes a mesma coisa.
   */
  const [salvoNoFormulario, setSalvoNoFormulario] = useState(0);

  /** Fecha o formulário e a revisão. Chamado depois de gravar, nos dois caminhos. */
  const fecharTudo = () => {
    setFormAberto(false);
    setSalvoNoFormulario(n => n + 1);
  };

  /**
   * Abre o formulário JÁ PREENCHIDO com o que foi interpretado.
   *
   * Clicar em "preencher manualmente" e encontrar campos vazios seria jogar
   * fora trabalho que já foi feito. O que veio do campo único entra; o resto
   * fica no default e ela completa.
   */
  const aplicarPrefill = (p: QuickEntryPrefill | null) => {
    if (p) {
      setFormData(prev => ({
        ...prev,
        type: p.type === 'income' ? TransactionType.INCOME : TransactionType.EXPENSE,
        description: p.description || prev.description,
        // O input é mascarado: converte o número para o formato que ele espera.
        value: p.value != null ? formatCurrency(String(Math.round(p.value * 100))) : prev.value,
        date: p.date || prev.date,
        // Só leva a compra quando ela é diferente — igual é o campo vazio.
        purchaseDate: p.purchaseDate && p.purchaseDate !== p.date ? p.purchaseDate : '',
        categoryId: p.categoryId ?? prev.categoryId,
        paymentMethod: p.paymentMethodId ?? prev.paymentMethod,
        hasInstallments: p.installments != null ? true : prev.hasInstallments,
        installments: p.installments != null ? String(p.installments) : prev.installments,
        // "3/6" interpretado no campo único chega aqui como 3. Cravar '1'
        // apagaria justamente o que ela disse ao abrir o formulário.
        currentInstallment: p.installments != null
          ? String(p.currentInstallment ?? 1)
          : prev.currentInstallment,
      }));
    }
    setFormAberto(true);
  };

  const gerenciador = useGerenciadorDeCategorias({
    categories,
    aoMudar: onCategoryCreated,
    aoCriar: (id: string) => setFormData(p => ({ ...p, categoryId: id })),
    aoExcluir: (id: string) => {
      if (formData.categoryId === id) setFormData(p => ({ ...p, categoryId: '' }));
      onCategoryDeleted?.();
    },
  });

  const selectedMethod = availableMethods.find(m => m.id === formData.paymentMethod);

  /*
   * O que será GRAVADO: a escolha explícita, senão o padrão do método, senão
   * pessoal. É a mesma precedência de `escopoDoLancamento` no servidor — e ela
   * está repetida aqui de propósito, porque o botão precisa mostrar o
   * resultado antes de salvar. Se as duas divergirem, a tela promete uma coisa
   * e o banco grava outra.
   */
  const escopoEfetivo: 'casa' | 'pessoal' =
    formData.escopo || selectedMethod?.escopoPadrao || 'pessoal';

  /*
   * Aparece para quem DIVIDE uma casa (05/10) — e não só para quem já marcou
   * um método como da casa. Quem acabava de aceitar um convite não tinha
   * método marcado nenhum, e a opção de dizer "este é da casa" não existia em
   * lugar nenhum. Ver `utils/temCasa.ts`.
   */
  const temCasa = useTemCasa();
  const mostrarEscopo =
    temCasa || availableMethods.some(m => m.escopoPadrao === 'casa') || escopoEfetivo === 'casa';
  const isEditingDebitTransaction = isSourceTransaction(editingTransaction);
  const billingDay = (!isEditingDebitTransaction && !debitSourceId) ? (selectedMethod?.billingDay ?? null) : null;
  /** O fechamento do método (15/09): decide em qual fatura a compra entra. */
  const closingDay = billingDay ? (selectedMethod?.closingDay ?? null) : null;

  /**
   * O botão da fatura: a data que está aí vira a COMPRA, e o pagamento vai
   * para o dia da fatura no mês seguinte.
   *
   * ── O que ele significava antes, e por que mudou (13/09) ─────────────────
   *
   * Antes ele trocava a data pelo dia de pagamento contado a partir de HOJE, e
   * a data que estava no campo — o dia em que ela comprou — sumia. Era a
   * perda exata que motivou as duas datas.
   *
   * Agora a compra é preservada: se o campo da compra está vazio, a compra é o
   * que estava no campo do pagamento; se já tem compra, é ela. Por isso clicar
   * duas vezes não anda o pagamento duas vezes — a âncora é a compra, que não
   * se move. É a mesma regra do campo único e do WhatsApp, presa ao backend
   * por `shared/dataDoCartao.casos.json`.
   *
   * ── Por que só no clique ─────────────────────────────────────────────────
   *
   * No formulário as duas datas estão à vista, e mudar a da compra NÃO move o
   * pagamento sozinho. Editar uma transação antiga não pode mudar o mês em que
   * ela pesa sem ela pedir — e o formulário grava o que tem na tela.
   *
   * Trocar o MÉTODO é pedir (15/09): `handleChange` recalcula as datas ali.
   * E num lançamento NOVO no cartão, preencher a data da compra também.
   */
  const applyBillingDay = () => {
    if (!billingDay) return;
    setFormData(prev => {
      const compra = prev.purchaseDate || prev.date || hojeISOLocal();
      return { ...prev, purchaseDate: compra, date: proximoPagamento(compra, billingDay, closingDay) };
    });
  };
  /*
   * A categoria lembrada, quando as categorias chegam DEPOIS do formulário.
   *
   * Métodos e categorias carregam cada um no seu tempo. Na abertura do app o
   * formulário nasce antes da lista de categorias, e `comUltimaEscolha` não
   * tem como conferir o id lembrado — sem isto o método voltava e a categoria
   * não. Só preenche campo VAZIO de lançamento novo em que ela ainda não
   * mexeu: "Sem categoria" escolhido à mão é escolha, não vazio.
   */
  const mexeuNaCategoria = useRef(false);
  useEffect(() => {
    if (editingTransaction || mexeuNaCategoria.current || categories.length === 0) return;
    setFormData(prev => {
      if (prev.categoryId) return prev;
      const lembrada = lerUltimaEscolha(prev.type === TransactionType.INCOME ? 'income' : 'expense');
      return lembrada?.categoryId && categories.some(c => c.id === lembrada.categoryId)
        ? { ...prev, categoryId: lembrada.categoryId }
        : prev;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories, editingTransaction]);

  /**
   * Gasto ↔ Recebimento.
   *
   * Num lançamento NOVO, o método e a categoria passam a ser os do último
   * lançamento daquele tipo — as datas voltam para a compra antes, para não
   * herdarem a fatura do cartão que estava escolhido. Na edição só o tipo
   * muda: os outros campos são dado gravado, não sugestão.
   */
  const trocarTipo = (tipo: TransactionType) => {
    setFormData(prev => {
      if (prev.type === tipo) return prev;
      if (editingTransaction || !lerUltimaEscolha(tipo === TransactionType.INCOME ? 'income' : 'expense')) {
        return { ...prev, type: tipo };
      }
      const compra = prev.purchaseDate || prev.date;
      return comUltimaEscolha({ ...prev, type: tipo, date: compra, purchaseDate: '' });
    });
  };

  /** A compra que o botão usaria agora — para o rótulo dizer o resultado antes do clique. */
  const compraDoBotao = formData.purchaseDate || formData.date || hojeISOLocal();

  useEffect(() => {
    if (editingTransaction) {
      setFormData({
        type: editingTransaction.type,
        paymentType: editingTransaction.paymentType,
        description: editingTransaction.description,
        value: editingTransaction.value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        hasInstallments: editingTransaction.hasInstallments,
        installments: editingTransaction.installments?.toString() || '',
        currentInstallment: editingTransaction.currentInstallment?.toString() || '',
        isMonthly: editingTransaction.isMonthly,
        endDate: editingTransaction.endDate ? editingTransaction.endDate.split('T')[0] : '',
        paymentMethod: editingTransaction.paymentMethod ?? DEFAULT_PAYMENT_METHOD,
        date: editingTransaction.date.split('T')[0],
        // CRU: vazio quando a linha não tem compra diferente. Preencher com a
        // data do pagamento faria salvar gravar uma compra que ninguém disse.
        purchaseDate: editingTransaction.purchaseDate ? editingTransaction.purchaseDate.split('T')[0] : '',
        categoryId: editingTransaction.categoryId || '',
        // Na edição a marca é a que a linha JÁ tem — não se herda de novo.
        escopo: editingTransaction.escopo ?? '',
      });
      setFormAberto(true); // editando: não há texto para interpretar
    } else {
      mexeuNaCategoria.current = false;
      setFormData(comUltimaEscolha(EMPTY_FORM));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingTransaction]);






  const formatCurrency = (raw: string): string => {
    const digits = raw.replace(/\D/g, '');
    if (!digits) return '';
    const cents = parseInt(digits, 10);
    return (cents / 100).toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const parseCurrency = (masked: string): number => {
    return parseFloat(masked.replace(/\./g, '').replace(',', '.')) || 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    if (aiFilledFields) setAiFilledFields(false);

    if (name === 'value') {
      setFormData(prev => ({ ...prev, value: formatCurrency(value) }));
      return;
    }
    /*
     * Trocar o método recalcula as datas (15/09).
     *
     * Pedido dela: "se eu mudar método de pagamento pra um que tenha data de
     * pagamento, aí tem que recalcular data". Mudar o método é ela pedindo —
     * diferente de mudar a data da compra, que continua não movendo nada
     * sozinha (ver `applyBillingDay`). Âncora na compra: trocar de volta ou
     * duas vezes para o mesmo cartão não anda o pagamento.
     */
    if (name === 'categoryId') mexeuNaCategoria.current = true;
    if (name === 'paymentMethod') {
      const metodo = availableMethods.find(m => m.id === value);
      setFormData(prev => {
        if (!prev.date) return { ...prev, paymentMethod: value };
        const d = datasAoTrocarMetodo(
          { date: prev.date, purchaseDate: prev.purchaseDate || null },
          metodo?.billingDay ?? null,
          metodo?.closingDay ?? null,
        );
        return { ...prev, paymentMethod: value, date: d.date, purchaseDate: d.purchaseDate ?? '' };
      });
      return;
    }
    /*
     * Lançamento NOVO no cartão: a data da compra já decide o pagamento (15/09).
     *
     * O pedido dela, confirmado: "se eu criar uma nova transação" com compra
     * depois do fechamento, "já vai automaticamente pro próximo mês?". Até
     * aqui só ia pelo botão da fatura — e com o cartão já escolhido, digitar a
     * compra deixava o pagamento no dia de hoje sem aviso.
     *
     * Só no NOVO. Na edição continua a regra de `applyBillingDay`: mudar uma
     * data de lançamento existente não move o mês em que ele pesa sem ela
     * pedir. E só a data da COMPRA: a do pagamento digitada à mão é o que ela
     * disse que é.
     */
    if (name === 'purchaseDate' && !editingTransaction && billingDay && value) {
      setFormData(prev => ({ ...prev, purchaseDate: value, date: proximoPagamento(value, billingDay, closingDay) }));
      return;
    }
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
      if (name === 'type') setDebitSourceId('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const transactionData: Omit<Transaction, 'id' | 'createdAt'> = {
      type: formData.type,
      paymentType: formData.paymentType,
      description: formData.description,
      value: parseCurrency(formData.value),
      hasInstallments: formData.hasInstallments,
      installments: formData.hasInstallments && formData.installments
        ? parseInt(formData.installments)
        : undefined,
      currentInstallment: formData.hasInstallments && formData.currentInstallment
        ? parseInt(formData.currentInstallment)
        : undefined,
      isMonthly: formData.isMonthly,
      // Item 16a: só faz sentido em recorrente, e '' vira null.
      endDate: formData.isMonthly ? (formData.endDate || null) : null,
      // Item 9b: a fonte vai em colunas próprias, não mais como rótulo
      // composto dentro de paymentMethod. Transferência de reserva não tem
      // método de pagamento — o backend grava NULL.
      paymentMethod: selectedDebitSource ? null : formData.paymentMethod,
      debitDirection: selectedDebitSource
        ? (formData.type === TransactionType.INCOME ? 'withdraw' as const : 'debit' as const)
        : null,
      debitSourceType: selectedDebitSource
        ? (selectedDebitSource.type === 'goal' ? 'goal' as const : 'investment' as const)
        : null,
      // O id da fonte no select é prefixado com inv_/goal_ para não colidir.
      debitSourceId: selectedDebitSource
        ? selectedDebitSource.id.replace(/^(inv_|goal_)/, '')
        : null,
      date: formData.date,
      // Vazio vai como null: "igual ao pagamento". O servidor também anula a
      // compra que for igual (`compraParaGravar`), então as duas pontas concordam.
      purchaseDate: formData.purchaseDate || null,
      categoryId: formData.categoryId || (null as any),
      // Só vai quando foi escolhido. Ausente = o servidor herda do método.
      ...(formData.escopo ? { escopo: formData.escopo } : {}),
    };

    /*
     * O modal da mensal só abre quando o VALOR mudou (15/09).
     *
     * Era "Apenas esta / Esta e as futuras" para qualquer edição, e o "Apenas
     * esta" estava quebrado: convertia o registro ORIGINAL (a data de início)
     * em avulsa e criava outra mensal a partir de início+1 — mudava o mês
     * errado, partia a mensal em duas e perdia fim, compra e lembrete.
     *
     * Agora a pergunta é a que importa quando o valor muda: foi só neste mês
     * (vira o valor do mês) ou o valor cadastrado mudou (vale daqui pra
     * frente)? Mudar descrição, categoria ou método não pergunta nada.
     */
    if (editingTransaction && editingTransaction.isMonthly && !editingTransaction.hasInstallments
        && Math.abs(Number(transactionData.value) - editingTransaction.value) > 0.001) {
      setPendingTransactionData(transactionData);
      setShowMonthlyModal(true);
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit(transactionData);

      // Débito de investimento ou reserva
      if (debitSourceId && !editingTransaction) {
        const source = debitSources.find(s => s.id === debitSourceId);
        if (source) {
          const amount = parseCurrency(formData.value);
          const newBalance = Math.max(source.balance - amount, 0);
          if (source.type === 'investment') {
            const inv = source.raw as Investment;
            await updateInvestment(inv.id, { ...inv, amount: newBalance });
          } else {
            const goal = source.raw as Goal;
            await updateGoal(goal.id, {
              type: goal.type, name: goal.name,
              targetAmount: goal.targetAmount,
              savedAmount: newBalance,
              categoryId: goal.categoryId ?? null,
              color: goal.color,
            });
          }
          onDebitApplied?.();
        }
      }

      if (!editingTransaction) {
        /*
         * Guarda o método e a categoria que ela acabou de usar. Só de
         * lançamento comum: transferência para reserva não tem método, e
         * lembrar "nenhum" faria o próximo gasto abrir sem cartão.
         */
        if (!selectedDebitSource && formData.paymentMethod) {
          guardarUltimaEscolha(
            formData.type === TransactionType.INCOME ? 'income' : 'expense',
            { paymentMethod: formData.paymentMethod, categoryId: formData.categoryId },
          );
        }
        mexeuNaCategoria.current = false;
        setFormData(comUltimaEscolha(EMPTY_FORM));
        setDebitSourceId('');
        setScanPreview(null);
        setScanError(null);
        setAiFilledFields(false);
      }
      fecharTudo();
    } catch (e) {
      // Ela viu o aviso de duplicata e desistiu: o formulário fica como está.
      // Qualquer outra falha continua subindo, como antes.
      if (!ehLancamentoCancelado(e)) throw e;
    } finally {
      setSubmitting(false);
    }
  };

  /*
   * "Só neste mês" precisa de um mês que já chegou e em que a mensal vale —
   * as mesmas regras do servidor (`podeInformar`). Senão só sobra "daqui pra
   * frente".
   */
  const podeSoNesteMes = !!mesExibido && !!editingTransaction
    && mesExibido <= hojeISOLocal().slice(0, 7)
    && mesExibido >= editingTransaction.date.slice(0, 7)
    && (!editingTransaction.endDate || mesExibido <= editingTransaction.endDate.slice(0, 7));
  /* Ver a nota em `utils/format.ts`: um formatador de moeda por componente é
     como o mesmo número saiu com ponto numa tela e vírgula na outra. */
  const brl = (v: number) => formatBRL(v);

  const handleMonthlyModalChoice = async (scope: 'this' | 'future') => {
    if (!pendingTransactionData) return;
    setShowMonthlyModal(false);
    setSubmitting(true);
    try {
      await onSubmit(pendingTransactionData, scope);
      setPendingTransactionData(null);
      fecharTudo();
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * O único caminho até a API de leitura — os dois botões passam por aqui.
   *
   * A redução acontece NESTE ponto, e não na escolha do arquivo, justamente
   * porque é aqui que os dois caminhos (foto inteira e recorte) se encontram.
   * Reduzir antes deixaria o recorte escapar: ele é feito num canvas em
   * resolução cheia e sairia grande do mesmo jeito.
   *
   * Antes disto a foto ia CRUA. Base64 infla 33%, então uma foto de celular de
   * 6MB virava 8MB de corpo — e já falhava, com um `alert('Tente novamente')`
   * que não explicava o motivo.
   */
  const processScanData = async (dataUrl: string, mimeType: string) => {
    setScanPreview(dataUrl);
    setScanError(null);
    setAiFilledFields(false);
    setScanning(true);
    try {
      const reduzida = await reduzirDataUrl(dataUrl);
      const base64 = reduzida.base64;
      /*
       * A foto é lida pelo caminho do campo único, que devolve UM LANÇAMENTO
       * POR LINHA (07/10). Antes ia por uma rota que só sabia ler um: a foto
       * de uma fatura virava o primeiro item, e o resto era perdido. Era o
       * que obrigava a fotografar compra por compra de um cartão que o Open
       * Finance não entrega.
       *
       * - Uma compra: preenche o formulário, como sempre.
       * - Várias: vão para a conferência do campo único, uma de cada vez.
       */
      const lido = await parseQuickEntry('', { base64, mimeType: reduzida.mimeType });
      if (lido.entries.length === 0) {
        setScanError('Não achei nenhuma compra nessa imagem. Tente uma foto mais nítida.');
        return;
      }
      if (lido.entries.length > 1) {
        setLeitura({ entries: lido.entries, vez: Date.now() });
        setFormAberto(false);
        setScanPreview(null);
        return;
      }
      const result = lido.entries[0];

      const formattedValue = result.value.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

      setFormData(prev => ({
        ...prev,
        description: result.description || prev.description,
        value: formattedValue || prev.value,
        date: result.date || prev.date,
        type: result.type === 'income' ? TransactionType.INCOME : TransactionType.EXPENSE,
        // Já vem resolvida contra as categorias DELA; sem palpite, fica vazia.
        categoryId: result.categoryId ?? '',
      }));
      setAiFilledFields(true);
      /*
       * O formulário ABRE com o que foi lido (08/10). Ele vive recolhido
       * atrás do campo único, e a foto preenchia os campos sem abri-lo: a
       * tela dizia "campos preenchidos pela IA" sem mostrar campo nenhum, e
       * ela não tinha onde conferir nem como lançar.
       */
      setFormAberto(true);
    } catch (err: any) {
      const msg = err?.response?.data?.error ?? 'Erro ao processar imagem.';
      setScanError(msg);
    } finally {
      setScanning(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Opens crop modal with the selected file
  const openCropModal = (file: File) => {
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target?.result as string;
      setCropData({ dataUrl, mimeType: file.type || 'image/jpeg' });
      setCropSel(null);
      setDragStart(null);
    };
    reader.readAsDataURL(file);
  };

  const handleScanImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) openCropModal(file);
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    if (editingTransaction || scanning) return;
    const items = Array.from(e.clipboardData.items);
    const imageItem = items.find(item => item.type.startsWith('image/'));
    if (!imageItem) return;
    const file = imageItem.getAsFile();
    if (file) {
      e.preventDefault();
      openCropModal(file);
    }
  };

  const [pasteHint, setPasteHint] = useState<'none' | 'ok' | 'empty'>('none');

  const handlePasteBtn = async () => {
    if (scanning) return;
    try {
      const items = await navigator.clipboard.read();
      for (const item of items) {
        const imgType = item.types.find(t => t.startsWith('image/'));
        if (imgType) {
          const blob = await item.getType(imgType);
          const file = new File([blob], 'paste.png', { type: imgType });
          openCropModal(file);
          return;
        }
      }
      // Nothing found
      setPasteHint('empty');
      setTimeout(() => setPasteHint('none'), 2000);
    } catch {
      // Fallback: browser may not support clipboard.read() — silently ignore
      setPasteHint('empty');
      setTimeout(() => setPasteHint('none'), 2000);
    }
  };

  // Crop interactions — shared by mouse and touch
  const getCropPoint = (clientX: number, clientY: number) => {
    const container = cropContainerRef.current;
    if (!container) return null;
    const rect = container.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(clientX - rect.left, rect.width)),
      y: Math.max(0, Math.min(clientY - rect.top, rect.height)),
    };
  };

  const cropDragStart = (clientX: number, clientY: number) => {
    const pt = getCropPoint(clientX, clientY);
    if (!pt) return;
    setDragStart(pt);
    setCropSel({ x: pt.x, y: pt.y, w: 0, h: 0 });
  };

  const cropDragMove = (clientX: number, clientY: number) => {
    if (!dragStart) return;
    const pt = getCropPoint(clientX, clientY);
    if (!pt) return;
    setCropSel({
      x: Math.min(dragStart.x, pt.x),
      y: Math.min(dragStart.y, pt.y),
      w: Math.abs(pt.x - dragStart.x),
      h: Math.abs(pt.y - dragStart.y),
    });
  };

  const cropDragEnd = () => setDragStart(null);

  // Mouse handlers
  const handleCropMouseDown = (e: React.MouseEvent) => { e.preventDefault(); cropDragStart(e.clientX, e.clientY); };
  const handleCropMouseMove = (e: React.MouseEvent) => { cropDragMove(e.clientX, e.clientY); };
  const handleCropMouseUp   = () => cropDragEnd();

  // Touch handlers
  const handleCropTouchStart = (e: React.TouchEvent) => {
    e.preventDefault();
    const t = e.touches[0];
    cropDragStart(t.clientX, t.clientY);
  };
  const handleCropTouchMove = (e: React.TouchEvent) => {
    e.preventDefault();
    const t = e.touches[0];
    cropDragMove(t.clientX, t.clientY);
  };
  const handleCropTouchEnd = () => cropDragEnd();

  const applyCrop = (useFullImage: boolean) => {
    if (!cropData) return;
    if (useFullImage || !cropSel || cropSel.w < 10 || cropSel.h < 10) {
      setCropData(null);
      processScanData(cropData.dataUrl, cropData.mimeType);
      return;
    }
    const img = cropImgRef.current;
    if (!img) return;
    const scaleX = img.naturalWidth / img.offsetWidth;
    const scaleY = img.naturalHeight / img.offsetHeight;
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(cropSel.w * scaleX);
    canvas.height = Math.round(cropSel.h * scaleY);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(
      img,
      Math.round(cropSel.x * scaleX),
      Math.round(cropSel.y * scaleY),
      canvas.width,
      canvas.height,
      0, 0, canvas.width, canvas.height,
    );
    const croppedDataUrl = canvas.toDataURL(cropData.mimeType);
    setCropData(null);
    setCropSel(null);
    processScanData(croppedDataUrl, cropData.mimeType);
  };

  const selectedDebitSource = debitSources.find(s => s.id === debitSourceId);
  const debitValue = parseCurrency(formData.value);
  const debitExceeds = selectedDebitSource ? debitValue > selectedDebitSource.balance : false;
  /* Formulário aberto: é o valor que ela está editando. Não obedece ao
     olhinho — ver a nota longa em `DebtProposal.tsx`. */
  const fmt = (v: number) => formatBRL(v);

  return (
    <div
      className={`transaction-form-container ${formData.type === TransactionType.INCOME ? 'accent-income' : 'accent-expense'}`}
      onPaste={handlePaste}
    >
      <Toast message={erroToast} onDismiss={() => setErroToast(null)} />
      <div className="tf-header">
        <h2>{editingTransaction ? 'Editar Transação' : 'Nova Transação'}</h2>
        <div className="tf-header-right">
          {!editingTransaction && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleScanImage}
              />
              <div id="tf-scan-btn-wrap" className="tf-action-tooltip-wrap" data-tooltip="Selecionar foto do comprovante">
                <button
                  type="button"
                  className="tf-scan-btn"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={scanning}
                  aria-busy={scanning}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                    <circle cx="12" cy="13" r="4"/>
                  </svg>
                  Scan
                </button>
              </div>
              <div id="tf-paste-btn-wrap" className="tf-paste-wrap tf-action-tooltip-wrap" data-tooltip="Colar imagem copiada (Ctrl+V)">
                <button
                  type="button"
                  className="tf-paste-btn"
                  onClick={handlePasteBtn}
                  disabled={scanning}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>
                    <rect x="8" y="2" width="8" height="4" rx="1" ry="1"/>
                  </svg>
                </button>
                {pasteHint === 'empty' && (
                  <span className="tf-paste-hint">Nenhuma imagem copiada</span>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Scan preview + feedback */}
      {scanPreview && !editingTransaction && (
        <div className="tf-scan-preview-wrap">
          <img src={scanPreview} alt="Comprovante" className="tf-scan-preview-img" />
          <div className="tf-scan-preview-info">
            {scanning && <span className="tf-scan-status-loading">Analisando imagem com IA...</span>}
            {!scanning && aiFilledFields && (
              <span className="tf-scan-status-ok">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                Campos preenchidos pela IA
              </span>
            )}
            {!scanning && scanError && (
              <span className="tf-scan-status-err">⚠ {scanError}</span>
            )}
            <button
              type="button"
              className="tf-scan-clear"
              onClick={() => { setScanPreview(null); setScanError(null); setAiFilledFields(false); }}
              title="Remover imagem"
            >✕</button>
          </div>
        </div>
      )}

      {!editingTransaction && (
        <>
          <QuickEntry
            categories={categories}
            paymentMethods={availableMethods}
            onCreate={async (data) => { await onSubmit(data as any); }}
            onOpenForm={aplicarPrefill}
            onCloseForm={() => setFormAberto(false)}
            formAberto={formAberto}
            onCategoryCreated={onCategoryCreated}
            onPaymentMethodCreated={() => { getPaymentMethods().then(setAvailableMethods); }}
            onCreateInvestment={onCreateInvestment}
            salvoNoFormulario={salvoNoFormulario}
            leitura={leitura}
            temCasa={temCasa}
          />
        </>
      )}

      {(formAberto || !!editingTransaction) && (
      <>
      {/* Acima do formulário, não na linha do título: é a primeira decisão do
          preenchimento manual, então pertence ao formulário — não ao cabeçalho
          do card. No campo único ele nem aparece, porque o tipo sai do texto. */}
      <div className="tf-type-toggle tf-type-toggle-form">
        <button
          type="button"
          className={`tf-toggle-btn expense ${formData.type === TransactionType.EXPENSE ? 'active' : ''}`}
          onClick={() => trocarTipo(TransactionType.EXPENSE)}
        >
          💸 Gasto
        </button>
        <button
          type="button"
          className={`tf-toggle-btn income ${formData.type === TransactionType.INCOME ? 'active' : ''}`}
          onClick={() => trocarTipo(TransactionType.INCOME)}
        >
          💰 Recebimento
        </button>
      </div>

      <form onSubmit={handleSubmit} className="transaction-form">
        <div className="form-row">
          <div className="form-group" style={{ flex: 2 }}>
            <label>Descrição *</label>
            <input
              type="text"
              maxLength={LIMITES.descricao}
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Ex: Aluguel, Salário, Compras..."
              required
            />
          </div>

          <div className="form-group">
            <label>Valor (R$) *</label>
            <input
              type="text"
              inputMode="numeric"
              name="value"
              value={formData.value}
              onChange={handleChange}
              placeholder="0,00"
              required
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>
              Data da compra <span style={{ fontWeight: 400, opacity: 0.7 }}>(opcional)</span>
              <span className="tf-tooltip-wrap">
                <span className="tf-tooltip-icon">?</span>
                <span className="tf-tooltip-text">
                  Quando você comprou. Não muda saldo nem o mês do gasto: é para você achar depois. Em branco, é a mesma data do pagamento.
                </span>
              </span>
            </label>
            <input
              type="date"
              onClick={abrirSeletorDeData}
              name="purchaseDate"
              value={formData.purchaseDate}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label>
              Data do pagamento *
              <span className="tf-tooltip-wrap">
                <span className="tf-tooltip-icon">?</span>
                <span className="tf-tooltip-text">
                  Data em que o pagamento foi ou será realizado. No caso de cartão de crédito, costuma ser a data de pagamento da fatura.
                </span>
              </span>
            </label>
            <input
              type="date"
              onClick={abrirSeletorDeData}
              name="date"
              value={formData.date}
              onChange={handleChange}
              required
            />
            {billingDay && (
              <button
                type="button"
                className="tf-billing-day-hint"
                onClick={applyBillingDay}
              >
                📅 Fatura dia {billingDay}{closingDay ? ` (fecha dia ${closingDay})` : ''}, pagar em {proximoPagamento(compraDoBotao, billingDay, closingDay).split('-').reverse().slice(0, 2).join('/')}
              </button>
            )}
          </div>

          <div className="form-group">
            <label>Natureza *</label>
            <select name="paymentType" value={formData.paymentType} onChange={handleChange} required>
              <option value={PaymentType.FIXED}>Fixo</option>
              <option value={PaymentType.VARIABLE}>Variável</option>
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Método de Pagamento *</label>
            {selectedDebitSource ? (
              <div className="tf-method-debit-badge">
                <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                  <path d="M6.5 1.5 L6.5 6.5 L9.5 9.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                  <circle cx="6.5" cy="6.5" r="5.5" stroke="currentColor" strokeWidth="1.4"/>
                </svg>
                {selectedDebitSource.name}
              </div>
            ) : (
              <select name="paymentMethod" value={formData.paymentMethod} onChange={handleChange} required>
                {availableMethods.map(method => (
                  <option key={method.id} value={method.id}>{method.name}</option>
                ))}
              </select>
            )}
          </div>

          <div className="form-group">
            <label>Categoria</label>
            <div className="tf-category-row">
              <select
                name="categoryId"
                value={formData.categoryId}
                onChange={handleChange}
                className="tf-category-select"
              >
                <option value="">Sem categoria</option>
                {categories.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            {/*
              * Os botões e os painéis saíram daqui em 30/08.
              *
              * Eram sete estados, quatro handlers e cem linhas de JSX vivendo
              * dentro do formulário. Ela pediu o mesmo no card de Categorias —
              * e a segunda cópia teria sido a armadilha 4 de novo, com duas
              * telas divergindo no dia em que uma ganhasse validação nova.
              *
              * `flex-wrap` na linha e `flex-basis: 100%` no painel fazem ele
              * cair na linha debaixo em vez de espremer o seletor.
              */}
            {gerenciador.botoes}
            </div>
          </div>
        </div>

        {/*
          * Os painéis FORA da linha do seletor.
          *
          * Dentro dela eles disputavam espaço com o `select` — e no card de
          * Categorias, onde o cabeçalho é `nowrap`, o estrago era pior: a lista
          * virava uma coluna espremida ao lado do botão "Lista".
          *
          * Botões pertencem a uma linha; painéis pertencem ao bloco abaixo
          * dela. São coisas diferentes e por isso vêm separados do hook.
          */}
        {gerenciador.paineis}

        {/*
          * A marca da casa — e ela só aparece quando FAZ diferença.
          *
          * O controle principal é o método de pagamento: marca-se "cartão da
          * casa" uma vez e tudo que passa nele nasce da casa. Isto aqui é a
          * CORREÇÃO — o aluguel pago no cartão pessoal, a compra sua no cartão
          * da casa.
          *
          * Correção só é necessária quando existe herança para corrigir, então
          * o seletor aparece quando algum método já está marcado (ou quando o
          * lançamento em edição já é da casa). Quem não usa a feature não ganha
          * um nono campo no formulário mais cheio do app.
          *
          * Transferência para reserva fica de fora: ela não conta como gasto da
          * casa em lugar nenhum (`contaComoGastoDaCasa` a exclui), e um
          * controle que não muda nada é uma promessa falsa.
          */}
        {mostrarEscopo && !selectedDebitSource && (
          <div className="form-row">
            <div className="form-group">
              <label>Este lançamento é</label>
              <div className="tf-escopo" role="group" aria-label="Da casa ou pessoal">
                {([
                  ['pessoal', 'Meu', 'Só você vê'],
                  ['casa', 'Da casa', 'Quem está na sua casa vê'],
                ] as const).map(([valor, rotulo, ajuda]) => (
                  <button
                    key={valor}
                    type="button"
                    className={`tf-escopo-op${escopoEfetivo === valor ? ' ativo' : ''}`}
                    aria-pressed={escopoEfetivo === valor}
                    title={ajuda}
                    onClick={() => setFormData(p => ({ ...p, escopo: valor }))}
                  >
                    {rotulo}
                  </button>
                ))}
              </div>
              {!formData.escopo && selectedMethod?.escopoPadrao === 'casa' && (
                <span className="tf-escopo-herda">
                  Herdado de <strong>{selectedMethod.name}</strong>
                </span>
              )}
            </div>
          </div>
        )}

        {/* Débito/retirada de investimento/reserva */}
        {debitSources.length > 0 && (
          <div className="form-row tf-debit-row">
            <div className="form-group tf-debit-group">
              <label className="tf-debit-label">
                {formData.type === TransactionType.INCOME ? (
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ marginRight: 5, verticalAlign: 'middle' }}>
                    <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M7 10V7l-2-2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    <path d="M7 7l2-2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                ) : (
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ marginRight: 5, verticalAlign: 'middle' }}>
                    <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M7 4v3l2 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                )}
                {formData.type === TransactionType.INCOME ? 'Retirar de' : 'Debitar de'}
              </label>
              <select
                value={debitSourceId}
                onChange={e => setDebitSourceId(e.target.value)}
                className={`tf-debit-select${debitExceeds ? ' tf-debit-over' : ''}`}
              >
                <option value="">
                  {formData.type === TransactionType.INCOME ? '— Não retirar —' : '— Não debitar —'}
                </option>
                {debitSources.some(s => s.type === 'investment') && (
                  <optgroup label="Investimentos">
                    {debitSources.filter(s => s.type === 'investment').map(s => (
                      <option key={s.id} value={s.id}>
                        {s.name} — {fmt(s.balance)}
                      </option>
                    ))}
                  </optgroup>
                )}
                {debitSources.some(s => s.type === 'goal') && (
                  <optgroup label="Reservas">
                    {debitSources.filter(s => s.type === 'goal').map(s => (
                      <option key={s.id} value={s.id}>
                        {s.name} — {fmt(s.balance)}
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
              {selectedDebitSource && (
                <div className={`tf-debit-info${debitExceeds ? ' tf-debit-info-over' : ''}`}>
                  {debitExceeds
                    ? `⚠️ Valor excede o saldo disponível de ${fmt(selectedDebitSource.balance)}`
                    : `Saldo após ${formData.type === TransactionType.INCOME ? 'retirada' : 'débito'}: ${fmt(selectedDebitSource.balance - debitValue)}`
                  }
                </div>
              )}
            </div>
          </div>
        )}

        <div className={`tf-checks-row${selectedDebitSource ? ' tf-checks-disabled' : ''}`}>
          <label className="tf-check-label" title={selectedDebitSource ? 'Indisponível ao debitar/retirar de investimento ou reserva' : undefined}>
            <input
              type="checkbox"
              name="hasInstallments"
              checked={selectedDebitSource ? false : formData.hasInstallments}
              onChange={handleChange}
              disabled={!!selectedDebitSource}
            />
            <span className="tf-check-box" />
            Parcelado
          </label>
          <label className="tf-check-label" title={selectedDebitSource ? 'Indisponível ao debitar/retirar de investimento ou reserva' : undefined}>
            <input
              type="checkbox"
              name="isMonthly"
              checked={selectedDebitSource ? false : formData.isMonthly}
              onChange={handleChange}
              disabled={!!selectedDebitSource}
            />
            <span className="tf-check-box" />
            Mensal
          </label>
          {selectedDebitSource && (
            <span className="tf-checks-debit-note">
              {formData.type === TransactionType.INCOME
                ? 'Retirada única: parcelado e mensal indisponíveis'
                : 'Débito único: parcelado e mensal indisponíveis'}
            </span>
          )}
        </div>

        {formData.isMonthly && !selectedDebitSource && (
          <div className="form-row">
            <div className="form-group">
              <label>Até quando? <span style={{ fontWeight: 400, opacity: 0.7 }}>(opcional)</span></label>
              <input
                type="date"
                onClick={abrirSeletorDeData}
                name="endDate"
                value={formData.endDate}
                onChange={handleChange}
                min={formData.date}
              />
              <small style={{ opacity: 0.7 }}>
                {formData.endDate
                  ? 'Conta até o mês desta data, inclusive.'
                  : 'Em branco = sem fim previsto. Preencha ao cancelar uma assinatura.'}
              </small>
            </div>
          </div>
        )}

        {formData.hasInstallments && (
          <div className="form-row">
            <div className="form-group">
              <label>Total de Parcelas</label>
              <input
                type="number"
                name="installments"
                value={formData.installments}
                onChange={handleChange}
                min="1"
                placeholder="Ex: 12"
              />
            </div>
            <div className="form-group">
              <label>Parcela Atual</label>
              <input
                type="number"
                name="currentInstallment"
                value={formData.currentInstallment}
                onChange={handleChange}
                min="1"
                placeholder="Ex: 3"
              />
            </div>
          </div>
        )}

        {/* Só em despesa, e só editando: o lembrete precisa de um lançamento
            que já existe para apontar. */}
        {editingTransaction && formData.type === TransactionType.EXPENSE && (
          <div className="tf-lembrete-wrap">
            <button
              type="button"
              className="tf-lembrete-btn"
              onClick={criarLembrete}
              disabled={criandoLembrete}
              aria-busy={criandoLembrete}
            >
              🔔 Criar lembrete para esta despesa
            </button>
            {lembreteAviso && <small className="tf-lembrete-aviso">{lembreteAviso}</small>}
          </div>
        )}

        <div className="form-actions">
          {editingTransaction && (
            <button type="button" onClick={onCancel} className="btn-cancel" disabled={submitting}>
              Cancelar
            </button>
          )}
          <button type="submit" className="btn-submit" disabled={submitting} aria-busy={submitting}>
            {editingTransaction ? 'Atualizar' : 'Adicionar'}
          </button>
        </div>
      </form>
      </>
      )}

      {/* Crop modal */}
      {cropData && (
        <div className="tf-crop-overlay">
          <div className="tf-crop-modal">
            <div className="tf-crop-modal-header">
              <h3>Recortar imagem</h3>
              <p className="tf-crop-hint-desktop">Arraste para selecionar a área com os dados da transação</p>
              <p className="tf-crop-hint-mobile">Toque e arraste para selecionar a área com os dados da transação</p>
            </div>
            <div className="tf-crop-canvas-wrap">
              <div
                ref={cropContainerRef}
                className="tf-crop-container"
                onMouseDown={handleCropMouseDown}
                onMouseMove={handleCropMouseMove}
                onMouseUp={handleCropMouseUp}
                onMouseLeave={handleCropMouseUp}
                onTouchStart={handleCropTouchStart}
                onTouchMove={handleCropTouchMove}
                onTouchEnd={handleCropTouchEnd}
              >
                <img
                  ref={cropImgRef}
                  src={cropData.dataUrl}
                  alt="Comprovante"
                  className="tf-crop-img"
                  draggable={false}
                />
                {cropSel && cropSel.w > 4 && cropSel.h > 4 && (
                  <>
                    <div className="tf-crop-dimmer tf-crop-dimmer-top"    style={{ height: cropSel.y }} />
                    <div className="tf-crop-dimmer tf-crop-dimmer-bottom" style={{ top: cropSel.y + cropSel.h }} />
                    <div className="tf-crop-dimmer tf-crop-dimmer-left"   style={{ top: cropSel.y, height: cropSel.h, width: cropSel.x }} />
                    <div className="tf-crop-dimmer tf-crop-dimmer-right"  style={{ top: cropSel.y, height: cropSel.h, left: cropSel.x + cropSel.w }} />
                    <div
                      className="tf-crop-selection"
                      style={{ left: cropSel.x, top: cropSel.y, width: cropSel.w, height: cropSel.h }}
                    />
                  </>
                )}
              </div>
            </div>
            <div className="tf-crop-actions">
              <button
                type="button"
                className="tf-crop-btn-full"
                onClick={() => applyCrop(true)}
              >
                Usar imagem completa
              </button>
              <button
                type="button"
                className={`tf-crop-btn-confirm${(!cropSel || cropSel.w < 10 || cropSel.h < 10) ? ' disabled' : ''}`}
                onClick={() => applyCrop(false)}
                disabled={!cropSel || cropSel.w < 10 || cropSel.h < 10}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                Recortar e analisar
              </button>
              <button
                type="button"
                className="tf-crop-btn-cancel"
                onClick={() => { setCropData(null); setCropSel(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {showMonthlyModal && (
        <div className="tf-monthly-modal-overlay">
          <div className="tf-monthly-modal">
            <div className="tf-monthly-modal-icon">🔁</div>
            <h3 className="tf-monthly-modal-title">O valor desta mensal mudou</h3>
            <p className="tf-monthly-modal-desc">
              De {brl(editingTransaction?.value ?? 0)} para {brl(Number(pendingTransactionData?.value ?? 0))}. Isso vale para:
            </p>
            <div className="tf-monthly-modal-actions">
              {podeSoNesteMes && (
                <button
                  type="button"
                  className="tf-monthly-btn tf-monthly-btn-this"
                  onClick={() => handleMonthlyModalChoice('this')}
                  disabled={submitting}
                >
                  <span className="tf-monthly-btn-icon">📌</span>
                  <span>
                    <strong>Só em {rotuloDoMes(mesExibido!)}</strong>
                    <small>Foi esse o valor do mês. O cadastrado continua {brl(editingTransaction?.value ?? 0)}.</small>
                  </span>
                </button>
              )}
              <button
                type="button"
                className="tf-monthly-btn tf-monthly-btn-future"
                onClick={() => handleMonthlyModalChoice('future')}
                disabled={submitting}
              >
                <span className="tf-monthly-btn-icon">📅</span>
                <span>
                  <strong>Daqui pra frente</strong>
                  <small>O valor cadastrado passa a ser este, e a média dos meses recomeça.</small>
                </span>
              </button>
            </div>
            <button
              type="button"
              className="tf-monthly-modal-cancel"
              onClick={() => { setShowMonthlyModal(false); setPendingTransactionData(null); }}
              disabled={submitting}
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionForm;
